import React from 'react';
import { Size } from '@components/Templates/utils/types';
import DesignWrapper from '../DesignWrapper';
import { DesignSetSize, LayoutCol, LayoutRow } from './Parts';
import { MAX_HEIGHT, MAX_WIDTH } from './constants';
import { DesignSizeTypeSetCategory } from 'graphql/generated/types.autogenerated';

type DesignSetProps = {
  sizes: Size[];
  layout: DesignSizeTypeSetCategory;
  name?: string;
  description?: string;
  url?: string;
  className?: string;
};

export default function DesignSet({ sizes, layout, name, description, url, className }: DesignSetProps) {
  function renderLayout() {
    switch (layout) {
      case DesignSizeTypeSetCategory.VIDEO:
        return <LayoutVideo sizes={sizes} />;
      case DesignSizeTypeSetCategory.SOCIAL_POST:
        return <LayoutSocialPost sizes={sizes} />;
      case DesignSizeTypeSetCategory.SOCIAL_CAMPAIGN:
        return <LayoutSocialCampaign sizes={sizes} />;
      default:
        return <LayoutDisplay sizes={sizes} />;
    }
  }

  const style = {
    // Dynamic height calculation based on aspect ratio
    paddingTop: `${(MAX_HEIGHT / MAX_WIDTH) * 100}%`,
  };

  return (
    <DesignWrapper url={url} name={name} description={description} className={className}>
      <div className="overflow-hidden rounded-lg bg-gray-100 p-[4px] sm:p-[5px] md:p-[4px] lg:p-[5px]">
        <div className="relative mx-auto" style={style}>
          <div className="absolute inset-0">{renderLayout()}</div>
        </div>
      </div>
    </DesignWrapper>
  );
}

// Width of a full row should equal MAX_WIDTH
// Height of a full column should equal MAX_HEIGHT

type DesignSetLayoutProps = {
  sizes: Size[];
};

function LayoutVideo({ sizes }: DesignSetLayoutProps) {
  const square = 124;
  const row1 = MAX_HEIGHT - square;
  const col1 = MAX_WIDTH - square;

  return (
    <LayoutRow>
      <LayoutCol width={square}>
        <DesignSetSize sizes={sizes} height={square} width={square} />
        <DesignSetSize sizes={sizes} height={row1} width={square} />
      </LayoutCol>
      <DesignSetSize sizes={sizes} height={MAX_HEIGHT} width={col1} isRow />
    </LayoutRow>
  );
}

function LayoutSocialPost({ sizes }: DesignSetLayoutProps) {
  const square = 122;
  const row1 = MAX_HEIGHT - square;
  const col1 = MAX_WIDTH - square;

  return (
    <LayoutRow>
      <DesignSetSize sizes={sizes} height={MAX_HEIGHT} width={col1} isRow />
      <LayoutCol width={square}>
        <DesignSetSize sizes={sizes} height={square} width={square} />
        <DesignSetSize sizes={sizes} height={row1} width={square} />
      </LayoutCol>
    </LayoutRow>
  );
}

function LayoutSocialCampaign({ sizes }: DesignSetLayoutProps) {
  const square = 138;
  const row1 = MAX_HEIGHT - square;
  const col1 = MAX_WIDTH - square;

  return (
    <LayoutRow>
      <LayoutCol width={square}>
        <DesignSetSize sizes={sizes} height={row1} width={square} />
        <DesignSetSize sizes={sizes} height={square} width={square} />
      </LayoutCol>
      <DesignSetSize sizes={sizes} height={MAX_HEIGHT} width={col1} isRow />
    </LayoutRow>
  );
}

function LayoutDisplay({ sizes }: DesignSetLayoutProps) {
  const row1 = 130;
  const row2 = MAX_HEIGHT - row1;
  const col1 = 170;
  const col2 = MAX_WIDTH - col1;

  return (
    <LayoutCol>
      <LayoutRow height={row1}>
        <DesignSetSize sizes={sizes} height={row1} width={col1} isRow />
        <DesignSetSize sizes={sizes} height={row1} width={col2} isRow />
      </LayoutRow>
      <DesignSetSize sizes={sizes} height={row2} width={MAX_WIDTH} />
    </LayoutCol>
  );
}
